/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { EuiLink } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyAdminProject } from '@modules/project-admin-api/types'

// eslint-disable-next-line import/no-restricted-paths
import { getConfigForKey } from '@/store'

const DASHBOARDS = {
  'serverless-project-id-overview': (
    <FormattedMessage
      id='project.link.serverless-project-id-overview'
      defaultMessage='Project overview'
    />
  ),
  'serverless-project-applications-logs': (
    <FormattedMessage
      id='project.link.serverless-project-applications-logs'
      defaultMessage='Project application logs'
    />
  ),
  'serverless-control-plane-services-logs': (
    <FormattedMessage
      id='project.link.serverless-control-plane-services-logs'
      defaultMessage='Control Plane services logs'
    />
  ),
  'serverless-es-project-stack-monitoring': (
    <FormattedMessage
      id='project.link.serverless-es-project-stack-monitoring'
      defaultMessage='Elasticsearch project stack monitoring'
    />
  ),
  'serverless-es-project-overview': (
    <FormattedMessage
      id='project.link.serverless-es-project-overview'
      defaultMessage='Elasticsearch project overview'
    />
  ),
  'agentless-logs': (
    <FormattedMessage id='project.link.agentless-logs' defaultMessage='Agentless logs' />
  ),
  'agentless-metrics': (
    <FormattedMessage id='project.link.agentless-metrics' defaultMessage='Agentless metrics' />
  ),
}

export type Props = {
  project: AnyAdminProject
  dashboard: keyof typeof DASHBOARDS
}

const ObservabilityDashboardLink: React.FunctionComponent<Props> = ({ project, dashboard }) => {
  const o11yDomain = getConfigForKey(`CLOUD_SERVERLESS_O11Y_DOMAIN`)

  if (!o11yDomain) {
    return null
  }

  let url: string

  // Special handling for agentless dashboards
  if (dashboard === 'agentless-logs') {
    // Agentless logs uses the project applications logs dashboard filtered by application id and project id
    url = `https://${o11yDomain}/app/dashboards#/view/serverless-project-applications-logs?_g=(filters:!((query:(match_phrase:(kubernetes.labels.k8s_elastic_co/application-id:agentless))),(query:(match_phrase:(serverless.project.id:${project.id})))))`
  } else if (dashboard === 'agentless-metrics') {
    // Agentless metrics uses its own dashboard filtered by project.id
    url = `https://${o11yDomain}/app/dashboards#/view/agentless-kubernetes-pods-dashboard-id?_g=(filters:!((query:(match_phrase:(serverless.project.id:${project.id})))))`
  } else {
    // Default URL format for other dashboards
    url = `https://${o11yDomain}/app/dashboards#/view/${dashboard}?_g=(filters:!((query:(match_phrase:(serverless.project.id:${project.id})))))`
  }

  return (
    <EuiLink external={true} href={url} target='_blank'>
      {DASHBOARDS[dashboard]}
    </EuiLink>
  )
}

export default ObservabilityDashboardLink
